import { FC } from "react";
import {
  Box,
  createStyles,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { Modal, TwoNumberInput } from "components";
import {
  MarketplaceMedian,
  MarketplaceMedianTitle,
} from "components/MarketplaceMedianModal";
import ParagraphContent from "components/Modal/ParagraphContent";
import CheckboxFilterGroup from "components/UseQueryParamsControls/FilterControls/CheckboxFilterGroup";
import FilterSectionTitle from "components/UseQueryParamsControls/FilterControls/FilterSectionTitle";
import RadioButtonFilterGroup from "components/UseQueryParamsControls/FilterControls/RadioButtonFilterGroup";
import RangeFilterControl from "components/UseQueryParamsControls/FilterControls/RangeFilterControl";
import ToggleFilterGroup from "components/UseQueryParamsControls/FilterControls/ToggleFilterGroup";
import { BLOG_WHAT_IS_DOLLAR_AGE_URL } from "constants/index";
import { FilterConfig } from "containers/BaseListings";
import theme from "theme";

import {
  ownerActiveFilterOptions,
  rangeOptionsDollarAge,
  rangeOptionsListPrice,
  rangeOptionsLtm,
  rangeOptionsMarketplaceMedian,
  termFilterOptions,
} from "./constants";

export const buildFilters = (filterConfig: FilterConfig): JSX.Element[] => {
  return [
    toggleFilterGroup(filterConfig),
    ownerActiveFilter(filterConfig),
    termFilter(filterConfig),
    !!filterConfig.userId ? marketplaceMedianFilter(filterConfig) : null,
    rangeLtmFilter(filterConfig),
    DollarAgeFilter(filterConfig),
    listPriceFilter(filterConfig),
  ].filter((element): element is JSX.Element => element !== null);
};

const toggleFilterGroup = (filterConfig: FilterConfig) => {
  return (
    <ToggleFilterGroup
      userId={filterConfig.userId}
      hideResetButton={filterConfig.hideResetButton}
      isDefaultUiParams={filterConfig.isDefaultUiParams}
      handleResetFilters={filterConfig.handleResetFilters}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      hideOnlyFilledListings
    />
  );
};

const ownerActiveFilter = (filterConfig: FilterConfig) => {
  return (
    <RadioButtonFilterGroup
      filterConfig={{
        title: "Owner Active in",
        filterOptions: ownerActiveFilterOptions,
        key: "owner_last_active_date.gte",
        makePostHogFilterBy: optionName =>
          `Only show listings with owner active in the ${optionName.toLowerCase()}`,
      }}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      modalInfo={{
        title: "Recent Owner Activity",
        label: [
          "An active owner is one who has responded to an offer or set a list price.",
        ],
      }}
    />
  );
};

const termFilter = (filterConfig: FilterConfig) => {
  return (
    <CheckboxFilterGroup
      filterConfig={{
        title: "Term",
        filterOptions: termFilterOptions,
        key: "term.in",
        makePostHogFilterBy: optionName => `Show ${optionName} listings`,
      }}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      modalInfo={{
        title: "Term",
        label: [
          "Term is the length of time an asset will pay. This includes partial 10 and 30 year terms for assets that are being resold.",
        ],
      }}
    />
  );
};

const marketplaceMedianFilter = (filterConfig: FilterConfig) => {
  return (
    <RangeFilterControl
      rangeKey="marketplace_median"
      rangeOptions={rangeOptionsMarketplaceMedian}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      title={
        <FilterSectionTitle
          title="Marketplace Median"
          HelpModal={({ onClose }) => (
            <Modal
              open
              title={<MarketplaceMedianTitle />}
              maxWidth="sm"
              onClose={onClose}
            >
              <MarketplaceMedian />
            </Modal>
          )}
        />
      }
      startAdornment={<InputAdornment position="start">$</InputAdornment>}
    />
  );
};

const rangeLtmFilter = (filterConfig: FilterConfig) => {
  return (
    <RangeFilterControl
      rangeKey="ltm"
      rangeOptions={rangeOptionsLtm}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      title={<FilterSectionTitle title="Last 12 Mo. Earnings" />}
      startAdornment={<InputAdornment position="start">$</InputAdornment>}
    />
  );
};

const useDollarAgeFilterStyles = makeStyles(() =>
  createStyles({
    moreInfo: {
      color: theme.custom.palette.blue.main,
      textDecoration: "underline",
    },
  }),
);

const DollarAgeFilter: FC<FilterConfig> = (filterConfig: FilterConfig) => {
  const classes = useDollarAgeFilterStyles();
  return (
    <RangeFilterControl
      rangeKey="dollar_age"
      rangeOptions={rangeOptionsDollarAge}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      title={
        <FilterSectionTitle
          title="Dollar Age"
          HelpModal={({ onClose }) => (
            <Modal open onClose={onClose} title="Dollar Age">
              <ParagraphContent
                paragraphs={[
                  "Dollar Age is a time-weighted metric based on the date each track in a catalog was released, and how much each earned over the last 12 months.",
                ]}
              />
              <Box textAlign="center">
                <Typography
                  className={classes.moreInfo}
                  variant="body2"
                  component="a"
                  href={BLOG_WHAT_IS_DOLLAR_AGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here for more information
                </Typography>
              </Box>
            </Modal>
          )}
        />
      }
      inputComponent={TwoNumberInput}
    />
  );
};

const listPriceFilter = (filterConfig: FilterConfig) => {
  return (
    <RangeFilterControl
      rangeKey="converted_list_price"
      rangeOptions={rangeOptionsListPrice}
      uiParams={filterConfig.uiParams}
      setUiParams={filterConfig.setUiParams}
      title={<FilterSectionTitle title="List Price" />}
      startAdornment={<InputAdornment position="start">$</InputAdornment>}
    />
  );
};
